import {
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type InsightCategory =
  | 'growth'
  | 'preventive'
  | 'behavioral'
  | 'lifestyle'
  | 'risk';
export type InsightTrend = 'improving' | 'stable' | 'needs-attention';
export type ConfidenceLevel = 'high' | 'medium' | 'low';

export interface AIInsight {
  id: string;
  category: InsightCategory;
  title: string;
  summary: string;
  details: string;
  trend: InsightTrend;
  confidence: ConfidenceLevel;
  dataPoints: string[];
  actionSuggestion?: string;
  updatedAt: string;
  helpful?: boolean;
}

interface AIInsightCardProps {
  insight: AIInsight;
  variant?: 'owner' | 'professional';
  onFeedback?: (insightId: string, helpful: boolean) => void;
  showActions?: boolean;
}

export function AIInsightCard({
  insight,
  variant = 'owner',
  onFeedback,
  showActions = true,
}: AIInsightCardProps) {
  const { t } = useTranslation();
  const categoryConfig = {
    growth: {
      label: t('features.aiInsights.growthWeight', 'Growth & Weight'),
      color: 'blue',
      icon: TrendingUp,
    },
    preventive: {
      label: t('features.aiInsights.preventiveCare', 'Preventive Care'),
      color: 'green',
      icon: AlertCircle,
    },
    behavioral: {
      label: t('features.aiInsights.behavioral', 'Behavioral Signals'),
      color: 'purple',
      icon: Info,
    },
    lifestyle: {
      label: t('features.aiInsights.lifestyle', 'Lifestyle & Activity'),
      color: 'orange',
      icon: Minus,
    },
    risk: {
      label: t('features.aiInsights.riskIndicators', 'Risk Indicators'),
      color: 'red',
      icon: AlertCircle,
    },
  };

  const trendConfig = {
    improving: {
      label: t('features.aiInsights.improving', 'Improving'),
      icon: TrendingUp,
      color: 'green',
    },
    stable: {
      label: t('features.aiInsights.stable', 'Stable'),
      icon: Minus,
      color: 'blue',
    },
    'needs-attention': {
      label: t('features.aiInsights.needsAttention', 'Needs Attention'),
      icon: TrendingDown,
      color: 'amber',
    },
  };

  const confidenceConfig = {
    high: {
      label: t('features.aiInsights.confidenceHigh', 'High Confidence'),
      color: 'green',
      percentage: '85-100%',
    },
    medium: {
      label: t('features.aiInsights.confidenceMedium', 'Medium Confidence'),
      color: 'yellow',
      percentage: '60-84%',
    },
    low: {
      label: t('features.aiInsights.confidenceLow', 'Low Confidence'),
      color: 'gray',
      percentage: '<60%',
    },
  };

  const config = categoryConfig[insight.category];
  const trend = trendConfig[insight.trend];
  const confidence = confidenceConfig[insight.confidence];
  const TrendIcon = trend.icon;

  return (
    <div className="bg-white dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8 hover:border-blue-300 dark:hover:border-blue-500/50 transition-all overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-white/8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  config.color === 'blue'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30'
                    : config.color === 'green'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-500/30'
                      : config.color === 'purple'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30'
                        : config.color === 'orange'
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {config.label}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  trend.color === 'green'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-500/30'
                    : trend.color === 'blue'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30'
                      : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <TrendIcon className="w-3 h-3" />
                {trend.label}
              </span>
            </div>
            <h4
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {insight.title}
            </h4>
          </div>
          <div
            className={`px-2 py-1 rounded-lg ${
              confidence.color === 'green'
                ? 'bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-500/30'
                : confidence.color === 'yellow'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-500/30'
                  : 'bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/20'
            }`}
          >
            <p
              className={`text-xs ${
                confidence.color === 'green'
                  ? 'text-green-700 dark:text-green-300'
                  : confidence.color === 'yellow'
                    ? 'text-yellow-700 dark:text-yellow-300'
                    : 'text-gray-700 dark:text-white/70'
              }`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {confidence.label}
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="p-4">
        <p
          className="text-gray-700 dark:text-white/90 mb-3"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {insight.summary}
        </p>

        {/* Details */}
        <div className="space-y-3">
          <div
            className="p-3 bg-gray-50 dark:bg-white/5 rounded-lg text-sm text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {insight.details}
          </div>

          {/* Data Points Used */}
          {variant === 'owner' && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-gray-500 dark:text-white/50" />
                <span
                  className="text-sm text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.aiInsights.dataUsedForInsight',
                    'Data used for this insight'
                  )}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {insight.dataPoints.map((point) => (
                  <span
                    key={point}
                    className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-500/30"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {point}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Suggestion */}
          {insight.actionSuggestion && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg">
              <p
                className="text-sm text-blue-900 dark:text-blue-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span className="font-medium">
                  {t('features.aiInsights.suggestionLabel', 'Suggestion')}:{' '}
                </span>
                {insight.actionSuggestion}
              </p>
            </div>
          )}

          {/* Low Confidence Warning */}
          {insight.confidence === 'low' && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p
                className="text-sm text-amber-800 dark:text-amber-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.aiInsights.lowConfidenceWarning',
                  'This insight has lower confidence due to limited data. Consider consulting with your veterinarian for professional evaluation.'
                )}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-white/8">
          <span
            className="text-xs text-gray-500 dark:text-white/50"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.aiInsights.updatedLabel', 'Updated')}{' '}
            {new Date(insight.updatedAt).toLocaleDateString()}
          </span>

          {showActions && onFeedback && (
            <div className="flex items-center gap-2">
              <span
                className="text-xs text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.aiInsights.helpfulQuestion', 'Helpful?')}
              </span>
              <button
                onClick={() => onFeedback(insight.id, true)}
                className={`p-1.5 rounded-lg transition-colors ${
                  insight.helpful === true
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-500/30'
                    : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/70 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 dark:hover:text-green-400'
                }`}
              >
                <ThumbsUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => onFeedback(insight.id, false)}
                className={`p-1.5 rounded-lg transition-colors ${
                  insight.helpful === false
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-500/30'
                    : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/70 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400'
                }`}
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
